<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-3xl mx-auto w-full px-4 py-8">
      <div v-if="loading" class="flex justify-center py-20">
        <div class="w-10 h-10 border-4 border-[#b99a52] border-t-transparent rounded-full animate-spin"></div>
      </div>

      <template v-else-if="service && provider">
        <!-- Cabeçalho -->
        <div class="mb-6">
          <router-link :to="{ name: 'provider-profile', params: { hash: provider.hash } }" class="text-sm text-[#b99a52] hover:underline flex items-center gap-1 mb-3">
            <span class="mdi mdi-arrow-left"></span> Voltar ao perfil
          </router-link>
          <h1 class="text-2xl font-bold text-[#152a34]">Agendar Serviço</h1>
        </div>

        <!-- Resumo do serviço -->
        <div class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 mb-6">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="font-semibold text-[#152a34] text-lg">{{ service.name }}</h2>
              <p class="text-sm text-gray-500">{{ provider.name }}</p>
              <p v-if="service.description" class="text-sm text-gray-400 mt-1">{{ service.description }}</p>
            </div>
            <div class="text-right">
              <p class="text-2xl font-bold text-[#152a34]">R$ {{ formatPrice(service.price_with_fee) }}</p>
              <p class="text-xs text-gray-400">{{ service.duration_minutes }} min</p>
            </div>
          </div>
        </div>

        <!-- Calendário -->
        <div class="mb-6">
          <h3 class="font-semibold text-[#152a34] mb-3">Escolha a Data e Horário</h3>
          <AppointmentCalendar
            :provider-hash="route.params.hash"
            :service-id="route.params.serviceId"
            @select="onSlotSelected"
          />
          <div v-if="selectedDatetime" class="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 flex items-center gap-2">
            <span class="mdi mdi-check-circle text-lg"></span>
            <span>Horário selecionado: <strong>{{ selectedDatetimeDisplay }}</strong></span>
          </div>
        </div>

        <!-- Endereço do contratante -->
        <div class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 mb-6">
          <h3 class="font-semibold text-[#152a34] mb-4">Endereço para o Serviço</h3>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div class="md:col-span-1">
              <label class="text-sm font-medium block mb-1">CEP *</label>
              <div class="flex gap-2">
                <input
                  v-model="address.zip_code"
                  @input="onCepInput"
                  type="text"
                  placeholder="00000-000"
                  maxlength="9"
                  inputmode="numeric"
                  :class="['flex-1 border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]', cepError ? 'border-red-300' : 'border-gray-200']"
                />
                <button @click="fetchCep" :disabled="cepLoading" class="px-3 py-2 bg-[#152a34] text-white rounded-xl text-sm hover:bg-[#1e3a4a] transition disabled:opacity-60">
                  <span :class="cepLoading ? 'mdi mdi-loading animate-spin' : 'mdi mdi-magnify'"></span>
                </button>
              </div>
              <p v-if="cepError" class="text-xs text-red-500 mt-1 flex items-center gap-1">
                <span class="mdi mdi-alert-circle text-sm"></span>
                {{ cepError }}
              </p>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <label class="text-sm font-medium block mb-1">Logradouro *</label>
              <input v-model="address.address" type="text" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Número *</label>
              <input v-model="address.number" type="text" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Complemento</label>
              <input v-model="address.complement" type="text" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Bairro *</label>
              <input v-model="address.neighborhood" type="text" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Cidade *</label>
              <input v-model="address.city" type="text" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]" />
            </div>
            <div>
              <label class="text-sm font-medium block mb-1">Estado *</label>
              <input v-model="address.state" type="text" maxlength="2" class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] uppercase" />
            </div>
          </div>
        </div>

        <!-- Observações -->
        <div class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 mb-6">
          <label class="text-sm font-medium text-[#152a34] block mb-2">Observações (opcional)</label>
          <textarea
            v-model="notes"
            rows="3"
            placeholder="Alguma informação importante para o prestador..."
            class="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52] resize-none"
          ></textarea>
        </div>

        <!-- Erro -->
        <div v-if="errorMsg" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {{ errorMsg }}
        </div>

        <!-- Botão confirmar -->
        <button
          @click="confirmBooking"
          :disabled="!canConfirm || submitting"
          class="w-full py-4 bg-[#b99a52] text-white rounded-xl font-bold text-base hover:bg-[#a08840] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <span v-if="submitting" class="mdi mdi-loading animate-spin"></span>
          {{ submitting ? 'Confirmando...' : 'Confirmar Agendamento' }}
        </button>
      </template>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api, { createAppointment, getProviderProfile } from '@/services/api'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import AppointmentCalendar from '@/components/AppointmentCalendar.vue'

const route = useRoute()
const router = useRouter()

const provider = ref(null)
const service = ref(null)
const loading = ref(true)

const selectedDatetime = ref(null)
const selectedDatetimeDisplay = ref('')

const address = ref({
  zip_code: '',
  address: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: ''
})

const notes = ref('')
const submitting = ref(false)
const errorMsg = ref('')
const cepLoading = ref(false)
const cepError = ref('')

const canConfirm = computed(() => {
  return selectedDatetime.value &&
    address.value.address &&
    address.value.number &&
    address.value.city &&
    address.value.state
})

function onSlotSelected ({ datetime, date, time }) {
  selectedDatetime.value = datetime
  const d = new Date(`${date}T${time}:00`)
  selectedDatetimeDisplay.value = d.toLocaleString('pt-BR', { dateStyle: 'full', timeStyle: 'short' })
}

function onCepInput (e) {
  const digits = e.target.value.replace(/\D/g, '').slice(0, 8)
  address.value.zip_code = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
  if (digits.length === 8) fetchCep()
}

async function fetchCep () {
  const cep = address.value.zip_code.replace(/\D/g, '')
  if (cep.length !== 8) return
  cepLoading.value = true
  cepError.value = ''
  try {
    const { data } = await api.get(`/cep?cep=${cep}`)
    if (data && !data.erro) {
      address.value.address = data.logradouro || ''
      address.value.neighborhood = data.bairro || ''
      address.value.city = data.localidade || ''
      address.value.state = data.uf || ''
    } else {
      cepError.value = 'CEP não encontrado. Preencha o endereço manualmente.'
    }
  } catch {
    cepError.value = 'Não foi possível consultar o CEP. Preencha o endereço manualmente.'
  } finally {
    cepLoading.value = false
  }
}

function formatPrice (p) {
  return Number(p).toFixed(2).replace('.', ',')
}

async function confirmBooking () {
  errorMsg.value = ''
  if (!canConfirm.value) return
  submitting.value = true

  try {
    const { data } = await createAppointment({
      service_id: parseInt(route.params.serviceId),
      scheduled_at: selectedDatetime.value,
      contractor_address: address.value,
      notes: notes.value || null
    })
    router.push({ name: 'appointment-details', params: { id: data.data.id }, query: { booked: '1' } })
  } catch (err) {
    errorMsg.value = err?.response?.data?.error || 'Erro ao confirmar agendamento.'
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  try {
    const { data } = await getProviderProfile(route.params.hash)
    provider.value = data.data
    service.value = (data.data.services || []).find(s => s.id == route.params.serviceId)
  } catch {
    provider.value = null
  } finally {
    loading.value = false
  }
})
</script>

